import { useState } from 'react';
import { useDeletePasskey } from '@/api/hooks/usePasskeys';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { getErrorMessage } from '@/utils/errorHandler';
import { toast } from 'sonner';
import { registerPasskey, getDeviceName } from '@/lib/webauthn';
import { KeyIcon, PlusIcon, TrashIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import type { Passkey } from '@/api/types';

const PASSKEY_OPTIONS_URL = '/api/passkeys/register/options';
const PASSKEY_RESULT_URL = '/api/passkeys/register';

interface PasskeysSectionProps {
    passkeys: Passkey[] | null;
    isLoading: boolean;
    error: Error | null;
    onRefresh: () => void;
}

function isWebAuthnSupported(): boolean {
    return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}

function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export function PasskeysSection({ passkeys: passkeysData, isLoading, error, onRefresh }: PasskeysSectionProps) {
    const deletePasskey = useDeletePasskey();
    const [isRegistering, setIsRegistering] = useState(false);
    const [passkeyToDelete, setPasskeyToDelete] = useState<Passkey | null>(null);

    const webAuthnSupported = isWebAuthnSupported();
    const passkeys = passkeysData ?? [];

    async function handleAddPasskey() {
        setIsRegistering(true);
        try {
            const deviceName = getDeviceName();
            await registerPasskey(PASSKEY_OPTIONS_URL, PASSKEY_RESULT_URL, deviceName);
            onRefresh();
            toast.success('Passkey registered successfully');
        } catch (err) {
            if (err instanceof Error) {
                if (err.name === 'NotAllowedError') {
                    toast.error('Passkey registration was cancelled');
                } else if (err.name === 'SecurityError') {
                    toast.error('Security error: Please ensure you are on a secure connection');
                } else if (err.name === 'InvalidStateError') {
                    toast.error(
                        'This passkey is already registered. It may have synced from another device via iCloud Keychain or a similar service.'
                    );
                } else {
                    toast.error(err.message || 'Failed to register passkey');
                }
            } else {
                toast.error('An unexpected error occurred');
            }
        } finally {
            setIsRegistering(false);
        }
    }

    async function handleDeletePasskey() {
        if (!passkeyToDelete) return;

        try {
            await deletePasskey.mutateAsync(passkeyToDelete.id);
            toast.success('Passkey deleted successfully');
            onRefresh();
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setPasskeyToDelete(null);
        }
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-secondary">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-gray-100 p-2 dark:bg-dark">
                        <KeyIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Passkeys</h2>
                </div>
                {webAuthnSupported && passkeys.length > 0 && (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleAddPasskey}
                        isLoading={isRegistering}
                        disabled={isRegistering}
                    >
                        <PlusIcon className="mr-1 h-4 w-4" />
                        Add
                    </Button>
                )}
            </div>

            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                Sign in securely using your device's built-in authentication like Face ID, Touch ID, or Windows Hello.
                Each passkey is unique to the device where it was registered.
            </p>

            <div className="mt-4">
                {!webAuthnSupported && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
                        <p className="font-medium">Passkeys not supported</p>
                        <p className="mt-1 text-sm">
                            Your browser doesn't support passkeys. Try using a modern browser like Chrome, Safari, Firefox, or Edge.
                        </p>
                    </div>
                )}

                {isLoading && (
                    <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                        Loading passkeys...
                    </div>
                )}

                {error && <ErrorAlert message={getErrorMessage(error)} />}

                {!isLoading && !error && passkeys.length === 0 && webAuthnSupported && (
                    <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center dark:border-gray-700">
                        <KeyIcon className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500" />
                        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                            No passkeys registered yet. Add a passkey to sign in quickly and securely without a password.
                        </p>
                        <Button
                            onClick={handleAddPasskey}
                            isLoading={isRegistering}
                            disabled={isRegistering}
                            className="mt-4"
                        >
                            <PlusIcon className="mr-2 h-4 w-4" />
                            Add Passkey
                        </Button>
                    </div>
                )}

                {!isLoading && !error && passkeys.length > 0 && (
                    <div className="space-y-2">
                        {passkeys.map((passkey) => (
                            <div
                                key={passkey.id}
                                className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-dark dark:bg-dark"
                            >
                                <div className="flex items-center gap-3">
                                    <DevicePhoneMobileIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {passkey.deviceName || 'Passkey'}
                                        </p>
                                        <div className="flex flex-wrap gap-x-3 text-xs text-gray-500 dark:text-gray-400">
                                            <span>Added {formatDate(passkey.createdAt)}</span>
                                            {passkey.lastUsedAt && (
                                                <span>Last used {formatDate(passkey.lastUsedAt)}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setPasskeyToDelete(passkey)}
                                    className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <DeleteConfirmDialog
                open={passkeyToDelete !== null}
                onClose={() => setPasskeyToDelete(null)}
                onConfirm={handleDeletePasskey}
                title="Delete Passkey"
                description={`Are you sure you want to delete "${passkeyToDelete?.deviceName || 'this passkey'}"? You won't be able to use it to sign in anymore.`}
                confirmLabel="Delete"
                isLoading={deletePasskey.isPending}
            />
        </div>
    );
}
